import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import { AxiosInstance } from 'axios';
import apiClient from '../services/api';

interface User {
  email: string;
  name?: string;
  picture?: string;
  sub: string;
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
  roles: string[];
  api: AxiosInstance;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // DEV ONLY: bypass auth for frontend-only testing
    if (process.env.REACT_APP_BYPASS_AUTH === 'true') {
      setUser({ email: 'dev@localhost', name: 'Dev User', sub: 'dev|123', roles: ['admin'], picture: '' });
      setIsLoading(false);
      return;
    }

    const fetchUser = async () => {
      try {
        const { data } = await apiClient.get(`/auth/users/me`);

        // Manually extract roles from the namespaced claim
        const roles = data['https://octopuslabs.matrixgeneration.ai/roles'] || [];

        const processedUser: User = { ...data, roles };
        setUser(processedUser);
      } catch (error) {
        console.log('Frontend fetchUser failed, user is not authenticated.');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, []);

  const logout = () => {
    window.location.href = `${apiClient.defaults.baseURL}/auth/logout`;
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    logout,
    roles: user?.roles || [],
    api: apiClient,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
